<template>
  <teleport to="body">
    <transition name="slide-up">
      <div v-if="open" class="fixed inset-0 z-[99999] flex items-end">
        <div class="absolute inset-0 bg-black/50" @click="handleBackdropClick"></div>

        <div
          class="pin-modal-container relative w-full p-0 md:p-6"
          :style="{ marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : undefined }"
        >
          <div
            v-if="mode === 'locked'"
            class="mx-auto w-full md:w-[420px] bg-[#F6F4F0] rounded-t-2xl md:rounded-2xl p-6 shadow-xl"
          >
            <div
              class="mx-auto mb-4 h-12 w-12 rounded-full bg-[#EDEAE5] flex items-center justify-center"
              aria-hidden="true"
            >
              <svg
                width="33"
                height="40"
                viewBox="0 0 33 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.09 0C18.7421 0 21.2857 1.05357 23.161 2.92893C25.0364 4.8043 26.09 7.34784 26.09 10V16C27.6813 16 29.2074 16.6321 30.3326 17.7574C31.4578 18.8826 32.09 20.4087 32.09 22V34C32.09 35.5913 31.4578 37.1174 30.3326 38.2426C29.2074 39.3679 27.6813 40 26.09 40H6.08997C4.49867 40 2.97254 39.3679 1.84733 38.2426C0.722107 37.1174 0.0899658 35.5913 0.0899658 34V22C0.0899658 20.4087 0.722107 18.8826 1.84733 17.7574C2.97254 16.6321 4.49867 16 6.08997 16V10C6.08997 7.34784 7.14353 4.8043 9.0189 2.92893C10.8943 1.05357 13.4378 0 16.09 0ZM16.09 24C15.0808 23.9997 14.1088 24.3808 13.3689 25.067C12.6289 25.7532 12.1757 26.6937 12.1 27.7L12.09 28C12.09 28.7911 12.3246 29.5645 12.7641 30.2223C13.2036 30.8801 13.8283 31.3928 14.5592 31.6955C15.2901 31.9983 16.0944 32.0775 16.8703 31.9231C17.6463 31.7688 18.359 31.3878 18.9184 30.8284C19.4778 30.269 19.8588 29.5563 20.0131 28.7804C20.1674 28.0044 20.0882 27.2002 19.7855 26.4693C19.4827 25.7384 18.97 25.1136 18.3122 24.6741C17.6544 24.2346 16.8811 24 16.09 24ZM16.09 4C14.4987 4 12.9725 4.63214 11.8473 5.75736C10.7221 6.88258 10.09 8.4087 10.09 10V16H22.09V10C22.09 8.4087 21.4578 6.88258 20.3326 5.75736C19.2074 4.63214 17.6813 4 16.09 4Z"
                  fill="black"
                />
              </svg>
            </div>

            <h2
              class="text-center text-[#3D3A35] font-extrabold text-[1.375rem] md:text-[2rem] lg:text-[2.25rem] leading-tight"
            >
              Uh oh! Access locked.
            </h2>

            <p
              class="text-center text-[#5F5A52] mt-2 text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-5 md:leading-7 lg:leading-8"
            >
              To keep your records safe, we've locked it for
              <span clas="font-bold">15 minutes</span>. Grab a coffee ☕ and try again later! You
              may also reset your PIN.
            </p>

            <p
              class="mt-3 text-center text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] text-[#8A5A3A] font-medium"
            >
              Locked for now. Back in <span class="tabular-nums">{{ displayCountdown }}</span
              >!
            </p>

            <div class="mt-6 space-y-3">
              <button
                class="w-full py-[16px] md:py-[18px] lg:py-[20px] rounded-full font-semibold text-[1rem] md:text-[1.25rem] lg:text-[1.375rem] transition-all bg-[#42362B] text-white active:scale-[0.98] disabled:opacity-50"
                @click="$emit('forgot')"
                :disabled="loading"
              >
                Reset PIN
              </button>

              <button
                class="w-full rounded-2xl py-3 px-4 md:py-4 md:px-6 lg:py-5 lg:px-8 bg-transparent text-[#3D3A35] font-semibold text-[1rem] md:text-[1.25rem] lg:text-[1.375rem] border border-[#3D3A35]/60 active:scale-95 transition disabled:opacity-50"
                @click="$emit('cancel')"
                :disabled="loading"
              >
                Return to home
              </button>
            </div>
          </div>

          <!-- ===== VERIFY MODE ===== -->
          <div
            v-else
            class="w-full bg-[#F6F4F0] px-[24px] py-[40px] shadow-xl rounded-t-2xl md:rounded-2xl"
          >
            <h2
              class="text-[1.5rem] md:text-[2.25rem] lg:text-[2.5rem] leading-[1.75rem] md:leading-[2.75rem] lg:leading-[3rem] tracking-[-0.75px] text-[#191817] font-black text-center"
            >
              <template v-if="variant === 'local-transfer-egiro'"> Verification </template>
              <template v-else>
                Access your personal <br />
                giving records
              </template>
            </h2>

            <p
              class="mt-[24px] md:mt-[32px] lg:mt-[36px] text-center text-[#373531] text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] tracking-[-0.5px]"
            >
              {{
                variant === 'local-transfer-egiro'
                  ? 'For security purposes, verify with your 4-digit PIN.'
                  : 'Enter PIN'
              }}
            </p>

            <div class="mt-[24px] md:mt-[32px] lg:mt-[36px]">
              <PinInput
                ref="pinInput"
                v-model="pin"
                :length="length"
                :has-error="!!errorMsg"
                :disabled="loading"
                :auto-submit="true"
                :input-class="pinInputClass"
                @complete="handlePinComplete"
              />
            </div>

            <div class="mt-3 min-h-[24px] md:min-h-[32px]">
              <p
                v-if="errorMsg"
                class="text-left text-sm md:text-base lg:text-lg text-[#EC4E20] font-medium"
                role="alert"
              >
                {{ errorMsg }}
              </p>
            </div>

            <div class="mt-8">
              <button
                class="w-full py-[16px] md:py-[18px] lg:py-[20px] border-solid rounded-full border-2 border-[#322A21] text-[#322A21] font-semibold text-[1rem] md:text-[1.25rem] lg:text-[1.375rem] hover:bg-gray-50 active:scale-[0.99] transition disabled:opacity-50"
                @click="$emit('cancel')"
                :disabled="loading"
              >
                Cancel
              </button>

              <div
                v-if="variant !== 'local-transfer-egiro'"
                class="mt-[24px] md:mt-[28px] lg:mt-[32px] text-center"
              >
                <button
                  class="text-[1rem] md:text-[1.25rem] lg:text-[1.375rem] leading-[1.5rem] md:leading-[1.75rem] lg:leading-[2rem] tracking-[-0.5px] text-[#454BEF] font-[700] underline disabled:opacity-50"
                  @click="$emit('forgot')"
                  :disabled="loading"
                >
                  Forgot PIN?
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { Keyboard } from '@capacitor/keyboard';
import { isIosNativeOnly } from '@/utils/platform-check';
import PinInput from '@/components/MyGiving/Common/PinInput.vue';

export default {
  name: 'PinOverlay',
  components: { PinInput },

  props: {
    open: { type: Boolean, default: false },
    mode: { type: String, default: 'verify' }, // 'verify' | 'locked'
    lockedUntil: { type: [String, Date, Number, null], default: null },
    length: { type: Number, default: 4 },
    allowBackdropClose: { type: Boolean, default: false }, // 🔧 NEW: Optional backdrop close
    variant: {
      type: String,
      default: 'local-transfer-egiro',
      validator: value => ['local-transfer-egiro', 'verify'].includes(value),
    },
  },

  emits: ['verify', 'cancel', 'forgot', 'lock-expired'],

  data() {
    return {
      pin: '',
      loading: false,
      errorMsg: '',
      countdownText: '',
      lockTimer: null,
      keyboardListeners: [],
      keyboardHeight: 0,
    };
  },

  computed: {
    pinInputClass() {
      return [
        'h-[72px] w-full text-center text-[1.5rem] font-semibold rounded-[12px]',
        'border-2 border-[#D1CEC9] bg-[#FFFFFF] text-[#433D31] outline-none transition',
        'focus:border-[#246BFD] focus:ring-2 focus:ring-[#246BFD] focus:ring-offset-2 focus:ring-offset-white',
      ].join(' ');
    },

    displayCountdown() {
      return this.countdownText || '15:00';
    },
  },

  watch: {
    open(isOpen) {
      if (isOpen && this.mode === 'verify') {
        this.resetVerifyMode();
      } else if (!isOpen) {
        // When overlay closes, ensure keyboard is dismissed and resize mode is reset
        this.cleanupKeyboard();
      }
    },

    mode(newMode) {
      if (newMode === 'verify') {
        this.resetVerifyMode();
      } else if (newMode === 'locked') {
        this.startLockTimer();
      }
    },

    lockedUntil: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.mode === 'locked') {
          this.startLockTimer();
        }
      },
    },
  },

  mounted() {
    if (this.mode === 'locked' && this.lockedUntil) {
      this.startLockTimer();
    }

    // Add keyboard listeners for iOS to synchronize modal position with keyboard.
    // The overlay positions itself with margin-bottom alone. It must NOT touch the
    // plugin's resize mode: capacitor.config.json sets `resize: "none"` for the whole
    // app, and an earlier version of this listener flipped the mode to `native` on
    // hide and never back, so every later keyboard also shrank the WebView and the
    // overlay rose by two keyboard heights (reported on iOS 27, 2026-10-18).
    if (isIosNativeOnly()) {
      Keyboard.addListener('keyboardWillShow', info => {
        // keyboardWillShow fires BEFORE animation starts, allowing synchronization
        this.keyboardHeight = info.keyboardHeight || 0;
      })
        .then(listener => {
          this.keyboardListeners.push(listener);
        })
        .catch(e => {
          console.log('Error adding keyboardWillShow listener:', e);
        });

      Keyboard.addListener('keyboardDidHide', () => {
        this.keyboardHeight = 0;
      })
        .then(listener => {
          this.keyboardListeners.push(listener);
        })
        .catch(e => {
          console.log('Error adding keyboardDidHide listener:', e);
        });
    }
  },

  beforeUnmount() {
    this.clearLockTimer();

    // Ensure keyboard cleanup before unmounting
    this.cleanupKeyboard();

    // Clean up keyboard listeners
    this.keyboardListeners.forEach(listener => {
      listener.remove();
    });
    this.keyboardListeners = [];
  },

  methods: {
    resetVerifyMode() {
      this.pin = '';
      this.errorMsg = '';
      this.loading = false;
      this.$nextTick(() => this.$refs.pinInput?.clearAndFocusFirst?.());
    },

    async handlePinComplete(value) {
      if (this.loading) return;

      this.loading = true;
      this.errorMsg = '';

      try {
        this.$emit('verify', value);
      } catch {
        this.loading = false;
      }
    },

    showError(message) {
      this.errorMsg = message;
      this.pin = '';
      this.loading = false;
      this.$nextTick(() => this.$refs.pinInput?.clearAndFocusFirst?.());
    },

    handleBackdropClick() {
      if (this.allowBackdropClose && !this.loading) {
        this.$emit('cancel');
      }
    },

    startLockTimer() {
      this.clearLockTimer();

      if (!this.lockedUntil) return;

      const tick = () => {
        const lockedTime = new Date(this.lockedUntil);
        const ms = lockedTime - new Date();

        if (ms <= 0) {
          this.countdownText = '';
          this.clearLockTimer();
          this.$emit('lock-expired');
          return;
        }

        const totalSec = Math.floor(ms / 1000);
        const m = String(Math.floor(totalSec / 60)).padStart(2, '0');
        const s = String(totalSec % 60).padStart(2, '0');
        this.countdownText = `${m}:${s}`;

        this.lockTimer = setTimeout(tick, 1000);
      };

      tick();
    },

    clearLockTimer() {
      if (this.lockTimer) {
        clearTimeout(this.lockTimer);
        this.lockTimer = null;
      }
    },

    async cleanupKeyboard() {
      // Blur any focused inputs to dismiss keyboard
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')
      ) {
        activeElement.blur();
      }

      // Also blur PIN input fields specifically
      if (this.$refs.pinInput && this.$refs.pinInput.pinRefs) {
        this.$refs.pinInput.pinRefs.forEach(ref => {
          if (ref && typeof ref.blur === 'function') {
            ref.blur();
          }
        });
      }

      // Reset keyboard height immediately
      this.keyboardHeight = 0;

      // Explicitly hide the keyboard as a safety measure (resize mode is left alone)
      if (isIosNativeOnly()) {
        try {
          await Keyboard.hide();
        } catch (e) {
          // Keyboard.hide() may not be available on all platforms, ignore errors
          if (process.env.NODE_ENV === 'development') {
            console.log('Keyboard.hide() not available or failed:', e);
          }
        }
      }
    },
  },
};
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

/* Synchronize keyboard height transition with iOS keyboard animation (0.25s) */
.pin-modal-container {
  transition: margin-bottom 0.25s ease-out;
}
</style>
